import React, { useState, useEffect, useRef } from "react";
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from "../state/auth.jsx";
import {
    Plus,
    Filter,
    ChevronDown,
    X,
    Check,
    Save,
    Trash2,
    Loader2,
    MessageSquare,
    Globe
} from "lucide-react";
import DateRangeFilter from "./DateRangeFilter";
import { messagesApi } from "../services/messagesApi";

// --- Hooks ---

const useContainerHeight = () => {
    const [containerHeight, setContainerHeight] = useState('calc(100vh - 180px)');
    const sectionRef = useRef(null);

    useEffect(() => {
        const calculateHeight = () => {
            if (!sectionRef.current) return;
            const viewportHeight = window.innerHeight;
            const sectionTop = sectionRef.current.getBoundingClientRect().top;
            // padding-bottom (approx 20px) + section padding/margins
            // Adjusting to fit exactly like Messages.jsx
            const availableHeight = viewportHeight - sectionTop - 20;
            setContainerHeight(`${Math.max(400, availableHeight)}px`);
        };

        calculateHeight();
        window.addEventListener('resize', calculateHeight);
        const timeout = setTimeout(calculateHeight, 100);
        return () => {
            window.removeEventListener('resize', calculateHeight);
            clearTimeout(timeout);
        };
    }, []);

    return { containerHeight, sectionRef };
};





const useSwipeToClose = (onClose, contentRef, scrollRef) => {
    const touchStart = useRef({ x: 0, y: 0 });
    const initialScrollTop = useRef(0);
    const isDragging = useRef(false);

    const onTouchStart = (e) => {
        touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        initialScrollTop.current = scrollRef?.current?.scrollTop || 0;
        isDragging.current = true;
        if (contentRef.current) {
            contentRef.current.style.transition = 'none';
        }
    };

    const onTouchMove = (e) => {
        if (!isDragging.current) return;
        const currentX = e.touches[0].clientX;
        const currentY = e.touches[0].clientY;
        const deltaX = currentX - touchStart.current.x;
        const deltaY = currentY - touchStart.current.y;

        // startedAtTop check is critical: only allow close gesture if it began at the top
        const startedAtTop = initialScrollTop.current <= 0;

        // SWIPE RIGHT (Move content right) - Threshold 10px to detect intent
        if (deltaX > 0 && Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
            if (e.cancelable) e.preventDefault();
            if (contentRef.current) {
                contentRef.current.style.transform = `translateX(${deltaX}px)`;
            }
        }
        // SWIPE DOWN (Move content down)
        else if (deltaY > 0 && Math.abs(deltaY) > Math.abs(deltaX) && startedAtTop) {
            if (e.cancelable) e.preventDefault();
            if (contentRef.current) {
                contentRef.current.style.transform = `translateY(${deltaY}px)`;
            }
        }
    };

    const onTouchEnd = (e) => {
        if (!isDragging.current) return;
        isDragging.current = false;

        const currentX = e.changedTouches[0].clientX;
        const currentY = e.changedTouches[0].clientY;
        const deltaX = currentX - touchStart.current.x;
        const deltaY = currentY - touchStart.current.y;

        const startedAtTop = initialScrollTop.current <= 0;

        // Logic to determining close vs reset
        let closeType = null;

        // Vertical Close - Only if started at top
        if (deltaY > 100 && Math.abs(deltaY) > Math.abs(deltaX) && startedAtTop) {
            closeType = 'vertical';
        }
        // Horizontal Close (Right Swipe)
        else if (deltaX > 100 && Math.abs(deltaX) > Math.abs(deltaY)) {
            closeType = 'horizontal';
        }

        if (closeType && contentRef.current) {
            // Animate out
            contentRef.current.style.transition = 'transform 0.3s ease-out';
            if (closeType === 'vertical') {
                contentRef.current.style.transform = 'translateY(100%)';
            } else {
                contentRef.current.style.transform = 'translateX(100%)';
            }

            // Wait for animation then close
            setTimeout(() => {
                onClose();
            }, 300);
        } else {
            // Reset
            if (contentRef.current) {
                contentRef.current.style.transition = 'transform 0.3s ease-out';
                contentRef.current.style.transform = '';
            }
        }
    };

    return { onTouchStart, onTouchMove, onTouchEnd };
};

// --- Helper Components ---

const FormField = ({
    label,
    placeholder,
    type = "text",
    isLight,
    multiline = false,
    value,
    readOnly = false,
    onChange
}) => {
    const baseClasses = `w-full bg-transparent border rounded-xl px-3 py-2.5 text-xs outline-none transition-all placeholder:text-opacity-50 ${readOnly ? "opacity-60 cursor-default" : "focus:ring-2 focus:ring-opacity-50"
        }`;

    const themeClasses = isLight
        ? `border-neutral-200 text-neutral-900 placeholder-neutral-400 focus:border-neutral-400 focus:ring-neutral-200`
        : `border-white/10 text-neutral-200 placeholder-neutral-500 focus:border-white/20 focus:ring-white/10`;

    const controlledValue = (value === null || value === undefined) ? "" : value;

    const inputProps = {
        className: `${baseClasses} ${themeClasses} ${type === "number" ? "no-spin" : ""}`,
        placeholder,
        readOnly,
        onChange,
        value: controlledValue,
        type: type === "date" ? "date" : type,
    };

    return (
        <div className="flex flex-col gap-1.5 w-full">
            <label className={`text-[10px] uppercase tracking-wider font-semibold ${isLight ? "text-neutral-400" : "text-neutral-500"}`}>
                {label}
            </label>
            {multiline ? (
                <textarea
                    rows={readOnly ? 2 : 3}
                    className={`${baseClasses} ${themeClasses} resize-none`}
                    {...inputProps}
                />
            ) : (
                <input
                    {...inputProps}
                />
            )}
        </div>
    );
};

const DateField = ({ label, isLight, value, readOnly, onChange }) => {
    const baseClasses = `w-full border rounded-xl px-3 py-2.5 text-xs outline-none transition-all ${readOnly ? "opacity-60 cursor-default" : "focus:ring-2 focus:ring-opacity-50"}`;
    const themeClasses = isLight
        ? `bg-white border-neutral-200 text-neutral-900 focus:border-neutral-400 focus:ring-neutral-200`
        : `bg-[#0A0C10] border-white/10 text-neutral-200 focus:border-white/20 focus:ring-white/10`;
    return (
        <div className="flex flex-col gap-1.5 w-full">
            <label className={`text-[10px] uppercase tracking-wider font-semibold ${isLight ? "text-neutral-400" : "text-neutral-500"}`}>{label}</label>
            <input
                type="date"
                className={`${baseClasses} ${themeClasses} ${!isLight ? 'dark-date' : ''} relative z-10`}
                value={value || ""}
                readOnly={readOnly}
                onChange={onChange}
                onClick={(e) => !readOnly && e.target.showPicker && e.target.showPicker()}
                style={{
                    colorScheme: isLight ? 'light' : 'dark',
                }}
            />
        </div>
    );
};

const SelectField = ({ label, isLight, value, onChange, options, readOnly = false }) => {
    const baseClasses = `w-full border rounded-xl px-3 py-2.5 text-xs outline-none transition-all cursor-pointer ${readOnly ? "opacity-60 cursor-default" : "focus:ring-2 focus:ring-opacity-50"}`;
    const themeClasses = isLight
        ? `bg-white border-neutral-200 text-neutral-900 focus:border-neutral-400 focus:ring-neutral-200`
        : `bg-[#1a1a1a] border-white/10 text-neutral-200 focus:border-white/20 focus:ring-white/10`;
    return (
        <div className="flex flex-col gap-1.5 w-full">
            <label className={`text-[10px] uppercase tracking-wider font-semibold ${isLight ? "text-neutral-400" : "text-neutral-500"}`}>{label}</label>
            <select
                className={`${baseClasses} ${themeClasses}`}
                value={value || ""}
                disabled={readOnly}
                onChange={onChange}
            >
                {options.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
            </select>
        </div>
    );
};

const StatusBadge = ({ status, isLight }) => {
    const statusConfig = {
        confirmed: { bg: isLight ? 'bg-emerald-100' : 'bg-emerald-500/20', text: isLight ? 'text-emerald-700' : 'text-emerald-400', label: 'Confirmed' },
        cancelled: { bg: isLight ? 'bg-red-100' : 'bg-red-500/20', text: isLight ? 'text-red-700' : 'text-red-400', label: 'Cancelled' },
    };
    const config = statusConfig[status] || statusConfig.confirmed;
    return (
        <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full ${config.bg} ${config.text}`}>
            {config.label}
        </span>
    );
};

const STATUS_OPTIONS = [
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'cancelled', label: 'Cancelled' },
];

const linkify = (text, isBot, isLight) => {
    if (!text) return text;
    let linkColor = (isBot && isLight) || (!isBot && !isLight) ? 'text-blue-300 underline' : 'text-blue-600 underline';
    const pattern = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s),]+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = pattern.exec(text)) !== null) {
        if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
        if (match[1] && match[2]) {
            parts.push(<a key={match.index} href={match[2]} target="_blank" rel="noopener noreferrer" className={`${linkColor} underline-offset-2 hover:opacity-80 transition-opacity`}>{match[1]}</a>);
        } else {
            parts.push(<a key={match.index} href={match[3]} target="_blank" rel="noopener noreferrer" className={`${linkColor} underline-offset-2 hover:opacity-80 transition-opacity break-all`}>{match[3]}</a>);
        }
        lastIndex = pattern.lastIndex;
    }
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts.length > 0 ? parts : text;
};

const formatChatTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatFullChatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
};

const getChatDateKey = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toISOString().split('T')[0];
};

const DetailField = ({ label, value, isLight, multiline }) => {
    return (
        <div className="flex flex-col gap-1.5 w-full">
            <label className={`text-[10px] uppercase tracking-wider font-semibold ${isLight ? "text-neutral-400" : "text-neutral-500"}`}>
                {label}
            </label>
            <div className={`w-full border rounded-xl px-3 py-2.5 text-sm font-medium break-words transition-all ${isLight
                ? "border-neutral-200 text-neutral-900"
                : "border-white/10 text-neutral-200"
                } ${multiline ? "whitespace-pre-wrap min-h-[80px]" : ""}`}>
                {value || "-"}
            </div>
        </div>
    );
};

// --- Main Application Component ---

const API_BASE_URL = process.env.REACT_APP_API_BASE;

export default function Reservations() {
    const { theme, properties = [] } = useOutletContext();
    const isLight = theme === "light";
    const navigate = useNavigate();
    const { user, tabActiveHotelId, api } = useAuth();

    const [showGuestForm, setShowGuestForm] = useState(false);

    // Loading and Error States
    const [loading, setLoading] = useState(true);
    const [fetchingMore, setFetchingMore] = useState(false);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    // Filter States
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const [filterCheckInFrom, setFilterCheckInFrom] = useState("");
    const [filterCheckInTo, setFilterCheckInTo] = useState("");
    const [filterCheckOutFrom, setFilterCheckOutFrom] = useState("");
    const [filterCheckOutTo, setFilterCheckOutTo] = useState("");

    // Pagination States
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(20);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const filterRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (filterRef.current && !filterRef.current.contains(event.target)) {
                setShowFilterMenu(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Guest & Edit States
    const [selectedGuest, setSelectedGuest] = useState(null);
    const [showGuestDetail, setShowGuestDetail] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editedGuestData, setEditedGuestData] = useState(null);
    const [activeTab, setActiveTab] = useState('details'); // 'details' | 'messages'
    const [modalMessages, setModalMessages] = useState([]);
    const [loadingModalMessages, setLoadingModalMessages] = useState(false);

    // Swipe Refs & Hooks
    const newGuestModalRef = useRef(null);
    const guestDetailModalRef = useRef(null);
    // New Refs for scroll containers
    const guestDetailScrollRef = useRef(null);

    // For NewGuest, the modal container is also the scroll container
    const newGuestSwipe = useSwipeToClose(() => setShowGuestForm(false), newGuestModalRef, newGuestModalRef);
    // For GuestDetail, the modal container is outer, content is inner scrollable
    const guestDetailSwipe = useSwipeToClose(() => setShowGuestDetail(false), guestDetailModalRef, guestDetailScrollRef);

    // Dynamic Height Hook
    const { containerHeight, sectionRef } = useContainerHeight();

    const fetchGuestMessages = async (phone, hotelId) => {
        if (!phone || !hotelId) return;
        setLoadingModalMessages(true);
        try {
            // Clean phone for lookup (remove +, etc)
            const cleanPhone = phone.replace(/\D/g, '');
            const data = await messagesApi.getMessages(api, cleanPhone, hotelId);
            setModalMessages(data || []);
        } catch (err) {
            console.error("Error fetching modal messages:", err);
            setModalMessages([]);
        } finally {
            setLoadingModalMessages(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'messages' && selectedGuest) {
            fetchGuestMessages(selectedGuest.phone, selectedGuest.hotelId || tabActiveHotelId);
        }
    }, [activeTab, selectedGuest]);

    // Guests data from API
    const [guests, setGuests] = useState([]);

    const [selectedProperty, setSelectedProperty] = useState("ALL");
    const [propertyDropdownOpen, setPropertyDropdownOpen] = useState(false);

    const defaultNewGuest = (checkInDate, hotelId) => {
        const prop = properties.find(p => p.id == hotelId) || properties[0];
        return {
            id: "new-" + Date.now(),
            name: "",
            surname: "",
            reservationCode: "",
            email: "",
            phone: "",
            guests: 1,
            channel: "",
            checkIn: checkInDate || new Date().toISOString().substring(0, 10),
            checkOut: new Date(new Date().getTime() + 86400000).toISOString().substring(0, 10),
            note: "",
            hotelId: hotelId || (prop?.id),
            hotelName: prop?.name || ""
        };
    };

    const [newGuestData, setNewGuestData] = useState(null);

    useEffect(() => {
        if (properties.length > 0 && !newGuestData) {
            setNewGuestData(defaultNewGuest());
        }
    }, [properties]);

    useEffect(() => {
        if (!newGuestData) return;
        const targetName = selectedProperty === "ALL" ? (properties[0]?.name || "") : selectedProperty;
        if (newGuestData.hotelName !== targetName) {
            setNewGuestData(prev => ({
                ...prev,
                hotelName: targetName,
            }));
        }
    }, [selectedProperty, properties, newGuestData?.id]); // Use id instead of the whole object to avoid loops


    useEffect(() => {
        if (showGuestDetail && selectedGuest) {
            setEditedGuestData({ ...selectedGuest });
        } else {
            setEditedGuestData(null);
            setIsEditing(false);
        }
    }, [showGuestDetail, selectedGuest]);


    const propertyOptions = [
        { label: "All properties", value: "ALL" },
        ...properties.map(p => ({ label: p.name, value: p.name })),
        { label: "Add a property…", value: "__add_property" },
    ];

    const selectedPropertyLabel =
        propertyOptions.find((opt) => opt.value === selectedProperty)?.label || "All properties";

    // Fetch reservations from API
    const isAllMode = tabActiveHotelId === 'all';

    const fetchReservations = async (hotelId, apiInstance, pageNum = 1) => {
        if (!hotelId || !apiInstance) return;
        
        if (pageNum === 1) {
            setLoading(true);
        } else {
            setFetchingMore(true);
        }
        setError(null);
        try {
            const endpoint = hotelId === 'all' ? '/reservations/all' : '/reservations';
            const params = hotelId === 'all'
                ? {
                    checkin_from: filterCheckInFrom,
                    checkin_to: filterCheckInTo,
                    checkout_from: filterCheckOutFrom,
                    checkout_to: filterCheckOutTo,
                    page: pageNum,
                    pageSize: pageSize
                }
                : {
                    hotelId: hotelId,
                    checkin_from: filterCheckInFrom,
                    checkin_to: filterCheckInTo,
                    checkout_from: filterCheckOutFrom,
                    checkout_to: filterCheckOutTo,
                    page: pageNum,
                    pageSize: pageSize
                };
            const res = await apiInstance.get(endpoint, { params });
            const result = res.data;
            if (result.success) {
                const newData = result.data || [];
                setGuests(prev => pageNum === 1 ? newData : [...prev, ...newData]);
                setTotalPages(result.totalPages || 1);
                setTotalCount(result.total || 0);
                setHasMore(pageNum < (result.totalPages || 1));
            } else {
                throw new Error(result.error || 'Failed to fetch reservations');
            }
        } catch (err) {
            console.error('Error fetching reservations:', err);
            setError(err.message);
        } finally {
            setLoading(false);
            setFetchingMore(false);
        }
    };

    // Reset to page 1 when filters or hotel changes
    useEffect(() => {
        setCurrentPage(1);
        setHasMore(true);
        setSelectedProperty("ALL");
        setGuests([]); // Clear list on reset to avoid stale data flicker
        fetchReservations(tabActiveHotelId, api, 1);
    }, [tabActiveHotelId, api, filterCheckInFrom, filterCheckInTo, filterCheckOutFrom, filterCheckOutTo]);

    const desktopScrollRef = useRef(null);
    const mobileScrollRef = useRef(null);

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        const nearBottom = scrollHeight - scrollTop - clientHeight < 100;
        if (nearBottom && hasMore && !loading && !fetchingMore) {
            const nextPage = currentPage + 1;
            setCurrentPage(nextPage);
            fetchReservations(tabActiveHotelId, api, nextPage);
        }
    };

    // Client-side property filter
    const visibleGuests = guests.filter((g) => {
        if (selectedProperty !== "ALL" && g.hotelName !== selectedProperty) return false;
        return true;
    });

    const handlePropertySelect = (value) => {
        if (value === "__add_property") {
            console.log("Add property flow will go here.");
            setPropertyDropdownOpen(false);
            return;
        }
        setSelectedProperty(value);
        setPropertyDropdownOpen(false);
    };



    const handleNewGuestChange = (field, value) => {
        setNewGuestData(prev => ({ ...prev, [field]: value }));
    };

    const saveNewGuest = async (hotelId, apiInstance) => {
        if (!newGuestData.reservationCode || !newGuestData.checkIn || !newGuestData.checkOut) {
            setError("Please fill in Reservation Code, Check-in, and Check-out fields.");
            return;
        }

        setSaving(true);
        setError(null);

        try {
            if (!hotelId || !apiInstance) {
                navigate('/login');
                return;
            }

            const res = await apiInstance.post('/reservations', {
                ...newGuestData,
                hotelId: hotelId
            });

            const result = res.data;

            if (result.success) {
                setNewGuestData(defaultNewGuest());
                setShowGuestForm(false);
                fetchReservations(hotelId, apiInstance); // Refresh list
            } else {
                throw new Error(result.error || 'Failed to create reservation');
            }
        } catch (err) {
            console.error('Error creating reservation:', err);
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };



    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        // Handle ISO format (2025-12-12T00:00:00.000Z) or YYYY-MM-DD
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return "";
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
            ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    };

    // Shorter date format for mobile cards
    const formatDateMobile = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return "";
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Format date for input fields (YYYY-MM-DD)
    const formatDateForInput = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return "";
        return d.toISOString().substring(0, 10);
    };

    // Prevent background scroll when modal is open
    useEffect(() => {
        if (showGuestForm || showGuestDetail) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
        return () => { document.body.style.overflow = "unset"; };
    }, [showGuestForm, showGuestDetail]);

    // Edit Logic
    const startEditing = () => {
        setIsEditing(true);
    };

    const cancelEditing = () => {
        setIsEditing(false);
        if (selectedGuest) {
            setEditedGuestData({ ...selectedGuest });
        }
    };

    const saveEditing = async (hotelId, apiInstance) => {
        if (!editedGuestData) return;

        setSaving(true);
        setError(null);

        try {
            if (!hotelId || !apiInstance) {
                navigate('/login');
                return;
            }

            const res = await apiInstance.put('/reservations', {
                ...editedGuestData,
                hotelId: hotelId
            });

            const result = res.data;

            if (result.success) {
                setSelectedGuest(result.data);
                setIsEditing(false);
                fetchReservations(hotelId, apiInstance); // Refresh list
            } else {
                throw new Error(result.error || 'Failed to update reservation');
            }
        } catch (err) {
            console.error('Error updating reservation:', err);
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleEditChange = (field, value) => {
        if (editedGuestData) {
            setEditedGuestData(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    const handleDeleteGuest = async (hotelId, apiInstance) => {
        if (!selectedGuest) return;

        if (!window.confirm(`Are you sure you want to delete the reservation for ${selectedGuest.name || ''} ${selectedGuest.surname || ''}?`)) {
            return;
        }

        setSaving(true);
        setError(null);

        try {
            if (!hotelId || !apiInstance) {
                navigate('/login');
                return;
            }

            const res = await apiInstance.delete('/reservations', {
                data: { id: selectedGuest.id, hotelId: hotelId }
            });

            const result = res.data;

            if (result.success) {
                setShowGuestDetail(false);
                setSelectedGuest(null);
                fetchReservations(hotelId, apiInstance); // Refresh list
            } else {
                throw new Error(result.error || 'Failed to delete reservation');
            }
        } catch (err) {
            console.error('Error deleting reservation:', err);
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <style>{`
        input[type="number"].no-spin::-webkit-outer-spin-button,
        input[type="number"].no-spin::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type="number"].no-spin {
          -moz-appearance: textfield;
        }

        input[type="date"] {
            font-size: 12px;
            cursor: pointer;
        }
        
        /* Fix for Firefox icon click issue - disable pointer events on icon so click passes to input */
        input[type="date"]::-webkit-calendar-picker-indicator,
        input[type="date"]::-moz-calendar-picker-indicator {
            pointer-events: none;
        }
        
        .theme-light input[type="date"] {
            color-scheme: light;
            color: #171717;
        }

        .theme-light input[type="date"]::-webkit-calendar-picker-indicator {
            width: 6px;
        }


        /* Bottom sheet animation for mobile modals */
        @keyframes slideUp {
            from {
                transform: translateY(100%);
                opacity: 0;
            }
            to {
                transform: translateY(0);
                opacity: 1;
            }
        }
        .animate-slide-up {
            animation: slideUp 0.3s ease-out;
        }

        /* Handle bar for bottom sheet */
        .bottom-sheet-handle {
            width: 36px;
            height: 4px;
            background: rgba(128, 128, 128, 0.4);
            border-radius: 2px;
            margin: 0 auto 12px;
        }

        .scroll-container {
            -webkit-overflow-scrolling: touch;
            overscroll-behavior: contain;
        }
      `}</style>


            <div className={(isLight ? "theme-light" : "theme-dark")}>
                {/* Main card */}
                <section
                    ref={sectionRef}
                    style={{ height: containerHeight }}
                    className={
                        "rounded-3xl border overflow-hidden flex flex-col relative " +
                        (isLight
                            ? "bg-white border-neutral-200 shadow-xl"
                            : "bg-[#0d0f12] border-white/5 shadow-2xl")
                    }
                >
                    {/* Toolbar */}
                    <div className={`px-6 py-5 border-b shrink-0 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center ${isLight ? "bg-white border-neutral-200/60" : "bg-[#12141a] border-neutral-800"}`}>
                        <div className="flex flex-col gap-1">
                            <h2 className="text-lg font-semibold tracking-tight flex items-center gap-2">
                                All Guests
                                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${isLight ? "bg-neutral-100 border-neutral-200 text-neutral-500" : "bg-neutral-800 border-neutral-700 text-neutral-400"}`}>
                                    {visibleGuests.length}
                                </span>
                            </h2>
                            <p className={`text-xs ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>
                                Overview of guest profiles.
                            </p>
                        </div>

                        <div className="flex items-center gap-3 w-full sm:w-auto">

                            {/* FILTER BUTTON & DROPDOWN */}
                            <div className="relative" ref={filterRef}>
                                <button
                                    onClick={() => setShowFilterMenu(!showFilterMenu)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-full border text-xs font-medium ${isLight
                                        ? "bg-white border-neutral-200 text-neutral-700"
                                        : "bg-neutral-800 border-neutral-700 text-neutral-300"
                                        }`}
                                >
                                    <Filter size={14} />
                                    <span>Filter</span>
                                    {(filterCheckInFrom || filterCheckInTo || filterCheckOutFrom || filterCheckOutTo) && (
                                        <div className="w-2 h-2 rounded-full bg-indigo-500 ml-1"></div>
                                    )}
                                </button>

                                {/* Mobile: Bottom sheet overlay */}
                                {showFilterMenu && (
                                    <>
                                        {/* Mobile backdrop */}
                                        <div
                                            className="sm:hidden fixed inset-0 bg-black/50 z-40"
                                            onClick={() => setShowFilterMenu(false)}
                                        />

                                        {/* Filter panel - bottom sheet on mobile, dropdown on desktop */}
                                        <div className={`
                                            fixed sm:absolute inset-x-0 bottom-0 sm:inset-x-auto sm:bottom-auto sm:top-full sm:right-0 sm:left-auto
                                            mt-0 sm:mt-2 p-5 sm:p-4 
                                            rounded-t-3xl sm:rounded-2xl 
                                            border-t sm:border shadow-xl z-50
                                            animate-slide-up sm:animate-none
                                            ${isLight ? "bg-white border-neutral-200" : "bg-[#0A0C10] border-white/10"}
                                            sm:w-80
                                        `}>
                                            {/* Mobile handle bar */}
                                            <div className="sm:hidden bottom-sheet-handle"></div>

                                            <h4 className={`text-sm sm:text-xs font-semibold mb-4 sm:mb-3 ${isLight ? "text-neutral-900" : "text-white"}`}>Filter by Date</h4>

                                            {/* Check-in Date Range */}
                                            <div className={`mb-4 pb-4 border-b ${isLight ? "border-neutral-200" : "border-white/10"}`}>
                                                <p className={`text-[11px] sm:text-[10px] uppercase tracking-wider font-semibold mb-2 ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>Check-in Date Range</p>
                                                <DateRangeFilter
                                                    from={filterCheckInFrom}
                                                    to={filterCheckInTo}
                                                    onApply={({ from: fromValue, to: toValue }) => {
                                                        setFilterCheckInFrom(fromValue || "");
                                                        setFilterCheckInTo(toValue || "");
                                                    }}
                                                    onClear={() => {
                                                        setFilterCheckInFrom("");
                                                        setFilterCheckInTo("");
                                                    }}
                                                    buttonClassName="w-full"
                                                    isLight={isLight}
                                                />
                                            </div>

                                            {/* Check-out Date Range */}
                                            <div className="mb-4">
                                                <p className={`text-[11px] sm:text-[10px] uppercase tracking-wider font-semibold mb-2 ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>Check-out Date Range</p>
                                                <DateRangeFilter
                                                    from={filterCheckOutFrom}
                                                    to={filterCheckOutTo}
                                                    onApply={({ from: fromValue, to: toValue }) => {
                                                        setFilterCheckOutFrom(fromValue || "");
                                                        setFilterCheckOutTo(toValue || "");
                                                    }}
                                                    onClear={() => {
                                                        setFilterCheckOutFrom("");
                                                        setFilterCheckOutTo("");
                                                    }}
                                                    buttonClassName="w-full"
                                                    isLight={isLight}
                                                />
                                            </div>

                                            <div className="flex justify-between items-center pt-3">
                                                <button
                                                    onClick={() => {
                                                        setFilterCheckInFrom("");
                                                        setFilterCheckInTo("");
                                                        setFilterCheckOutFrom("");
                                                        setFilterCheckOutTo("");
                                                    }}
                                                    className={`text-xs underline ${isLight ? "text-neutral-500" : "text-neutral-400"}`}
                                                >
                                                    Clear All
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        fetchReservations(tabActiveHotelId, api);
                                                        setShowFilterMenu(false);
                                                    }}
                                                    className={`text-xs font-bold px-4 py-2 rounded-lg ${isLight ? "bg-neutral-900 text-white" : "bg-white text-neutral-900"}`}
                                                >
                                                    Done
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className={`h-8 w-[1px] hidden md:block ${isLight ? "bg-neutral-200" : "bg-white/10"}`}></div>

                            <button
                                onClick={() => {
                                    setNewGuestData(defaultNewGuest());
                                    setShowGuestForm(true);
                                }}
                                disabled={isAllMode}
                                className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold shadow-lg ${isLight
                                    ? "bg-neutral-900 text-white"
                                    : "bg-white text-neutral-900"
                                    } ${isAllMode ? 'opacity-40 cursor-not-allowed' : ''}`}
                                title={isAllMode ? 'Select a specific property to add a reservation' : ''}
                            >
                                <Plus size={14} strokeWidth={3} />
                                <span>Add Reservation</span>
                            </button>
                        </div>
                    </div>



                    {/* Content Area */}
                    <div
                        className={`flex-1 flex flex-col overflow-hidden scrollbar-hide scroll-container ${isLight ? "bg-white" : "bg-[#0d0f12]"}`}
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                        {/* Error Banner */}
                        {error && (
                            <div className={`mx-4 mt-4 p-3 rounded-xl text-xs ${isLight ? "bg-red-50 text-red-600 border border-red-200" : "bg-red-900/20 text-red-400 border border-red-800/30"}`}>
                                {error}
                                <button
                                    onClick={() => setError(null)}
                                    className="ml-2 underline"
                                >
                                    Dismiss
                                </button>
                            </div>
                        )}

                        {/* Loading State */}
                        {(loading && (currentPage === 1 || guests.length === 0)) ? (
                            <div className="flex flex-col items-center justify-center py-20">
                                <Loader2 size={32} className="animate-spin opacity-50 mb-2" />
                                <p className={`text-sm ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>Loading reservations...</p>
                            </div>
                        ) : visibleGuests.length === 0 && !loading && !fetchingMore ? (
                            <div className="flex flex-col items-center justify-center py-20 opacity-50">
                                <Filter size={32} className="mb-2" />
                                <p className="text-sm">No reservations found.</p>
                            </div>
                        ) : (
                            <>
                                {/* Desktop Table View - hidden on mobile */}
                                <div className="hidden lg:flex lg:flex-col lg:flex-1 min-w-[800px] overflow-hidden">
                                    {/* Table Header */}
                                    <div
                                        className={
                                            "sticky top-0 z-10 grid grid-cols-12 px-6 py-3 text-[10px] uppercase tracking-wider font-semibold border-b " +
                                            (isLight
                                                ? "bg-neutral-100 border-neutral-200 text-neutral-500"
                                                : "bg-[#0d0f12] border-white/5 text-neutral-500")
                                        }
                                    >
                                        <div className="col-span-3">Guest Profile</div>
                                        <div className="col-span-2">Reservation Code</div>
                                        <div className="col-span-2">Contact</div>
                                        <div className="col-span-1 text-center">Guests</div>
                                        <div className="col-span-2">Stay Dates</div>
                                        <div className="col-span-1">Status</div>
                                        <div className="col-span-1">Notes</div>
                                    </div>

                                    {/* Table Body */}
                                    <div 
                                        ref={desktopScrollRef}
                                        onScroll={handleScroll}
                                        className="pb-10 flex-1 overflow-y-auto overflow-x-hidden custom-scroll"
                                    >
                                        {visibleGuests.map((g, index) => (
                                            <div
                                                key={g.id}
                                                onClick={() => {
                                                    setSelectedGuest(g);
                                                    setShowGuestDetail(true);
                                                }}
                                                className={
                                                    "group grid grid-cols-12 px-6 py-4 text-xs border-b transition-all cursor-pointer items-center " +
                                                    (isLight
                                                        ? `border-neutral-200 hover:bg-neutral-200 text-neutral-600 ${index % 2 === 0 ? "bg-white" : "bg-neutral-50"}`
                                                        : "border-white/5 hover:bg-white/[0.02] text-neutral-400")
                                                }
                                            >
                                                <div className="col-span-3 pr-4 flex items-center gap-3">
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold ${isLight ? "bg-neutral-100 text-neutral-600" : "bg-white/10 text-neutral-300"
                                                        }`}>
                                                        {(g.name || '')[0] || ''}{(g.surname || '')[0] || ''}
                                                    </div>
                                                    <div className="flex flex-col min-w-0">
                                                        <span className={`font-semibold truncate ${isLight ? "text-neutral-900" : "text-neutral-200"}`}>
                                                            {g.name || ''} {g.surname || ''}
                                                        </span>
                                                        <div className="flex items-center gap-2 mt-0.5">
                                                            <span className="text-[10px] opacity-50 truncate">{g.channel || ''}</span>
                                                            {isAllMode && g.hotelName && (
                                                                <>
                                                                    <span className="w-1 h-1 rounded-full bg-neutral-300 opacity-40"></span>
                                                                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-tight ${isLight ? 'bg-blue-50 text-blue-600 border border-blue-100' : 'bg-blue-500/10 text-blue-400 border border-blue-500/10'}`}>
                                                                        {g.hotelName}
                                                                    </span>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-span-2 pr-4">
                                                    <span className={`font-medium ${isLight ? "text-neutral-900" : "text-neutral-200"}`}>{g.reservationCode}</span>
                                                </div>

                                                <div className="col-span-2 flex flex-col justify-center min-w-0 pr-4">
                                                    <span className="truncate">{g.email}</span>
                                                    <span className="text-[10px] opacity-60 truncate">{g.phone}</span>
                                                </div>

                                                <div className="col-span-1 text-center font-medium">
                                                    {g.guests}
                                                </div>

                                                <div className="col-span-2 flex flex-col justify-center text-[11px]">
                                                    <div className="flex items-center gap-1.5">
                                                        <span className={isLight ? "text-neutral-400" : "text-neutral-600"}>In:</span>
                                                        <span className="font-medium">{formatDate(g.checkIn)}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1.5">
                                                        <span className={isLight ? "text-neutral-400" : "text-neutral-600"}>Out:</span>
                                                        <span className="font-medium">{formatDate(g.checkOut)}</span>
                                                    </div>
                                                </div>

                                                <div className="col-span-1">
                                                    <StatusBadge status={g.status} isLight={isLight} />
                                                </div>

                                                <div className="col-span-1 pr-4">
                                                    <p className="truncate opacity-80 italic max-w-[120px]">{g.note}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    
                                    {/* Desktop Info Footer */}
                                    <div className={`px-6 py-3 flex items-center justify-between border-t shrink-0 ${isLight ? "border-neutral-200 bg-neutral-50/30" : "border-white/5 bg-white/5"}`}>
                                        <div className={`text-[10px] font-medium ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>
                                            Showing <span className={isLight ? "text-neutral-900" : "text-white"}>{visibleGuests.length}</span> of <span className={isLight ? "text-neutral-900" : "text-white"}>{totalCount}</span> reservations
                                        </div>
                                        {fetchingMore && (
                                            <div className="flex items-center gap-2">
                                                <Loader2 size={12} className="animate-spin opacity-50" />
                                                <span className="text-[10px] opacity-50">Loading more...</span>
                                            </div>
                                        )}
                                        {!hasMore && totalCount > 0 && (
                                            <div className="text-[10px] opacity-40 font-medium">All loaded</div>
                                        )}
                                    </div>
                                </div>

                                {/* Mobile Card View */}
                                <div className="lg:hidden flex flex-col h-full overflow-hidden">
                                    {/* Mobile Header */}
                                    {/* Mobile Header Removed - Moved to fixed position */}

                                    {/* Card List */}
                                    <div 
                                        ref={mobileScrollRef}
                                        onScroll={handleScroll}
                                        className="flex-1 overflow-y-auto pt-4 pb-20 px-4 space-y-3 custom-scroll"
                                    >
                                        {visibleGuests.map((g) => (
                                            <div
                                                key={g.id}
                                                onClick={() => {
                                                    setSelectedGuest(g);
                                                    setShowGuestDetail(true);
                                                }}
                                                className={
                                                    "rounded-2xl p-4 border cursor-pointer " +
                                                    (isLight
                                                        ? "bg-white border-neutral-100 shadow-sm"
                                                        : "bg-[#1a1d21] border-white/10")
                                                }
                                            >
                                                {/* Header: Name & Channel */}
                                                <div className="flex justify-between items-start mb-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${isLight ? "bg-neutral-100 text-neutral-700" : "bg-white/10 text-neutral-300"}`}>
                                                            {(g.name || '')[0] || ''}{(g.surname || '')[0] || ''}
                                                        </div>
                                                        <div>
                                                            <h4 className={`font-bold text-base leading-tight ${isLight ? "text-neutral-900" : "text-neutral-100"}`}>
                                                                {g.name || ''} {g.surname || ''}
                                                            </h4>
                                                            <span className={`text-[10px] uppercase tracking-wider font-medium ${isLight ? "text-neutral-400" : "text-neutral-500"}`}>
                                                                {g.reservationCode}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {g.channel && (
                                                        <span className={`text-[10px] font-medium px-2 py-1 rounded-md border ${isLight ? "bg-neutral-50 border-neutral-100 text-neutral-500" : "bg-white/5 border-white/10 text-neutral-400"}`}>
                                                            {g.channel}
                                                        </span>
                                                    )}
                                                    {isAllMode && g.hotelName && (
                                                        <span className={`text-[10px] font-semibold px-2 py-1 rounded-md border ${isLight ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-blue-500/10 border-blue-500/20 text-blue-400'}`}>
                                                            {g.hotelName}
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Divider */}
                                                <div className={`h-px w-full my-3 ${isLight ? "bg-neutral-100" : "bg-neutral-700"}`}></div>

                                                {/* Dates Row */}
                                                <div className="grid grid-cols-2 gap-4 mb-3">
                                                    <div>
                                                        <span className={`text-[10px] uppercase tracking-wider block mb-1 ${isLight ? "text-neutral-400" : "text-neutral-500 text-white"}`}>Check-in</span>
                                                        <div className={`text-sm font-semibold flex items-center gap-1.5 ${isLight ? "text-neutral-700" : "text-neutral-300"}`}>
                                                            {/* <Calendar size={12} className="opacity-50" /> */}
                                                            {formatDateMobile(g.checkIn)}
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <span className={`text-[10px] uppercase tracking-wider block mb-1 ${isLight ? "text-neutral-400" : "text-neutral-500 text-white"}`}>Check-out</span>
                                                        <div className={`text-sm font-semibold flex items-center justify-end gap-1.5 ${isLight ? "text-neutral-700" : "text-neutral-300"}`}>
                                                            {formatDateMobile(g.checkOut)}
                                                            {/* <Calendar size={12} className="opacity-50" /> */}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Footer Info */}
                                                <div className={`flex items-center justify-between pt-2 ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>
                                                    <div className="flex items-center gap-1.5 text-xs">
                                                        <div className={`flex items-center gap-1 px-2 py-1 rounded-md ${isLight ? "bg-neutral-50" : "bg-neutral-800"}`}>
                                                            <span className="font-medium">{g.guests}</span>
                                                            <span className="text-[10px] opacity-70">{g.guests === 1 ? 'Guest' : 'Guests'}</span>
                                                        </div>
                                                    </div>

                                                    {/* Status Badge */}
                                                    <StatusBadge status={g.status} isLight={isLight} />
                                                </div>
                                            </div>
                                        ))}
                                        {fetchingMore && (
                                            <div className={`py-4 text-center text-xs font-medium ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>
                                                Loading more...
                                            </div>
                                        )}
                                        
                                        {!hasMore && totalCount > 0 && (
                                            <div className={`py-8 text-center text-[10px] font-bold uppercase tracking-widest opacity-20 ${isLight ? "text-neutral-900" : "text-white"}`}>
                                                ~ All caught up ~
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </section>
            </div>

            {/* --- MODALS --- */}

            {/* New Guest Modal */}
            {showGuestForm && (
                <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm sm:p-4 animate-in fade-in duration-200">
                    <div
                        ref={newGuestModalRef}
                        {...newGuestSwipe}
                        className={
                            "w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl border-t sm:border p-6 shadow-2xl animate-slide-up sm:animate-none sm:scale-100 max-h-[90vh] overflow-y-auto custom-scroll " +
                            (isLight ? "bg-white border-neutral-200" : "bg-[#0A0C10] border-white/10")
                        }
                    >
                        {/* Mobile handle bar */}
                        <div className="sm:hidden w-full flex justify-center pt-1 pb-1">
                            <div className="bottom-sheet-handle mb-3"></div>
                        </div>
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h3 className={`text-lg font-semibold tracking-tight ${isLight ? "text-neutral-900" : "text-white"}`}>New Guest</h3>
                                <p
                                    className={
                                        "mt-1 text-xs " +
                                        (isLight ? "text-neutral-500" : "text-neutral-400")
                                    }
                                >
                                    Add a guest profile to {selectedProperty === 'ALL' ? 'a property' : newGuestData.property}.
                                </p>
                            </div>
                            <button
                                onClick={() => setShowGuestForm(false)}
                                className={
                                    "h-8 w-8 rounded-full flex items-center justify-center text-xs border transition-colors " +
                                    (isLight
                                        ? "bg-neutral-50 border-neutral-200 text-neutral-700 hover:bg-neutral-100"
                                        : "bg-white/5 border-white/15 text-neutral-200 hover:bg-white/10")
                                }
                            >
                                <X size={14} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <FormField
                                    label="First name"
                                    placeholder="Emma"
                                    isLight={isLight}
                                    value={newGuestData.name}
                                    onChange={(e) => handleNewGuestChange('name', e.target.value)}
                                />
                                <FormField
                                    label="Last name"
                                    placeholder="Johnson"
                                    isLight={isLight}
                                    value={newGuestData.surname}
                                    onChange={(e) => handleNewGuestChange('surname', e.target.value)}
                                />
                            </div>

                            <FormField
                                label="Reservation Code *"
                                placeholder="RES-2025-001"
                                isLight={isLight}
                                value={newGuestData.reservationCode}
                                onChange={(e) => handleNewGuestChange('reservationCode', e.target.value)}
                            />

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <FormField
                                    label="Email"
                                    placeholder="guest@example.com"
                                    type="email"
                                    isLight={isLight}
                                    value={newGuestData.email}
                                    onChange={(e) => handleNewGuestChange('email', e.target.value)}
                                />
                                <FormField
                                    label="Mobile phone"
                                    placeholder="+30 69..."
                                    isLight={isLight}
                                    value={newGuestData.phone}
                                    onChange={(e) => handleNewGuestChange('phone', e.target.value)}
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <FormField
                                    label="Number of guests"
                                    placeholder="1"
                                    type="number"
                                    isLight={isLight}
                                    value={newGuestData.guests}
                                    onChange={(e) => handleNewGuestChange('guests', parseInt(e.target.value))}
                                />
                                <FormField
                                    label="Booking channel"
                                    placeholder="Booking.com, Airbnb..."
                                    isLight={isLight}
                                    value={newGuestData.channel}
                                    onChange={(e) => handleNewGuestChange('channel', e.target.value)}
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <DateField
                                    label="Check-in date"
                                    isLight={isLight}
                                    value={newGuestData.checkIn}
                                    onChange={(e) => handleNewGuestChange('checkIn', e.target.value)}
                                />
                                <DateField
                                    label="Check-out date"
                                    isLight={isLight}
                                    value={newGuestData.checkOut}
                                    onChange={(e) => handleNewGuestChange('checkOut', e.target.value)}
                                />
                            </div>

                            <FormField
                                label="Notes / internal comments"
                                placeholder="VIP guest, arrival at 18:30, airport transfer needed…"
                                isLight={isLight}
                                multiline
                                value={newGuestData.note}
                                onChange={(e) => handleNewGuestChange('note', e.target.value)}
                            />
                        </div>

                        <div className="mt-8 flex flex-col-reverse sm:flex-row justify-end gap-3 text-xs font-medium">
                            <button
                                onClick={() => setShowGuestForm(false)}
                                className={
                                    "px-4 py-2.5 rounded-xl border transition-colors " +
                                    (isLight
                                        ? "bg-white border-neutral-200 text-neutral-700 hover:bg-neutral-50"
                                        : "bg-transparent border-white/15 text-neutral-300 hover:bg-white/5")
                                }
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => saveNewGuest(tabActiveHotelId, api)}
                                className={
                                    "px-6 py-2.5 rounded-xl shadow-lg transition-transform active:scale-95 " +
                                    (isLight ? "bg-neutral-900 text-white" : "bg-white text-neutral-900")
                                }
                            >
                                Save Guest
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Guest Detail / Edit Modal */}
            {showGuestDetail && selectedGuest && editedGuestData && (
                <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm sm:p-4 animate-in fade-in duration-200">
                    <div
                        ref={guestDetailModalRef}
                        {...guestDetailSwipe}
                        className={
                            "w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl border-t sm:border p-0 shadow-2xl overflow-hidden animate-slide-up sm:animate-none sm:scale-100 max-h-[90vh] flex flex-col " +
                            (isLight ? "bg-white border-neutral-200" : "bg-[#0A0C10] border-white/10")
                        }
                    >
                        {/* Mobile handle bar */}
                        <div className="sm:hidden pt-3 w-full flex justify-center">
                            <div className="bottom-sheet-handle"></div>
                        </div>
                        {/* Header */}
                        <div className={`px-6 py-4 border-b flex justify-between items-center ${isLight ? "bg-neutral-50/50 border-neutral-100" : "bg-white/[0.02] border-white/5"}`}>
                            <div className="flex items-center gap-3">
                                {isEditing ? (
                                    <div className="flex flex-col">
                                        <h3 className={`text-sm font-bold ${isLight ? "text-neutral-900" : "text-white"}`}>Editing Booking</h3>
                                        <span className="text-[10px] opacity-60">Update details below</span>
                                    </div>
                                ) : (
                                    <>
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shadow-sm ${isLight ? "bg-white text-neutral-800 border border-neutral-100" : "bg-white/10 text-white border border-white/10"}`}>
                                            {(selectedGuest.name || '')[0] || ''}{(selectedGuest.surname || '')[0] || ''}
                                        </div>
                                        <div>
                                            <h3 className={`text-sm font-bold ${isLight ? "text-neutral-900" : "text-white"}`}>
                                                {selectedGuest.name || ''} {selectedGuest.surname || ''}
                                            </h3>
                                            <span className={`text-[10px] uppercase tracking-wider font-medium ${isLight ? "text-neutral-400" : "text-neutral-500"}`}>
                                                {selectedGuest.property || ''}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                {!isEditing && (
                                    <button
                                        onClick={() => handleDeleteGuest(tabActiveHotelId, api)}
                                        className={`p-2 rounded-full border transition-colors ${isLight ? "border-neutral-200 text-red-500 hover:text-red-700 hover:bg-white" : "border-white/10 text-red-400 hover:text-red-200 hover:bg-white/5"}`}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                )}
                                <button
                                    onClick={() => {
                                        setShowGuestDetail(false);
                                    }}
                                    className={`p-2 rounded-full border transition-colors ${isLight ? "border-neutral-200 text-neutral-400 hover:text-neutral-700 hover:bg-white" : "border-white/10 text-neutral-500 hover:text-neutral-200 hover:bg-white/5"}`}
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        </div>

                        {/* Tab Switcher */}
                        {!isEditing && (
                            <div className={`flex border-b flex-shrink-0 ${isLight ? "border-neutral-100 bg-neutral-50/50" : "border-white/5 bg-white/[0.01]"}`}>
                                <button 
                                    onClick={() => setActiveTab('details')}
                                    className={`flex-1 py-3 text-[10px] font-bold uppercase tracking-widest transition-all relative ${activeTab === 'details' ? (isLight ? 'text-neutral-900' : 'text-white') : 'text-neutral-500 hover:text-neutral-400'}`}
                                >
                                    Details
                                    {activeTab === 'details' && <div className={`absolute bottom-0 left-0 right-0 h-0.5 ${isLight ? "bg-neutral-900" : "bg-white"}`}></div>}
                                </button>
                                <button 
                                    onClick={() => setActiveTab('messages')}
                                    className={`flex-1 py-3 text-[10px] font-bold uppercase tracking-widest transition-all relative ${activeTab === 'messages' ? (isLight ? 'text-neutral-900' : 'text-white') : 'text-neutral-500 hover:text-neutral-400'}`}
                                >
                                    Messages
                                    {activeTab === 'messages' && <div className={`absolute bottom-0 left-0 right-0 h-0.5 ${isLight ? "bg-neutral-900" : "bg-white"}`}></div>}
                                </button>
                            </div>
                        )}

                        <div ref={guestDetailScrollRef} className="p-6 space-y-5 overflow-y-auto custom-scroll flex-1">
                            {/* View Mode - Details */}
                            {!isEditing && activeTab === 'details' ? (
                                <>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <DetailField label="First Name" value={selectedGuest.name} isLight={isLight} />
                                        <DetailField label="Last Name" value={selectedGuest.surname} isLight={isLight} />
                                    </div>
                                    <DetailField label="Reservation Code" value={selectedGuest.reservationCode} isLight={isLight} />
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <DetailField label="Email" value={selectedGuest.email} isLight={isLight} />
                                        <DetailField label="Phone" value={selectedGuest.phone} isLight={isLight} />
                                    </div>
                                    <div className="p-4 rounded-xl border relative overflow-hidden group">
                                        <div className={`absolute inset-0 opacity-[0.03] ${isLight ? "bg-neutral-900" : "bg-white"}`}></div>
                                        <div className="relative z-10 grid grid-cols-2 gap-6">
                                            <div>
                                                <span className={`text-[10px] uppercase tracking-wider block mb-1 ${isLight ? "text-neutral-400" : "text-neutral-500"}`}>Check-in</span>
                                                <span className={`text-sm font-semibold ${isLight ? "text-neutral-900" : "text-white"}`}>{formatDate(selectedGuest.checkIn)}</span>
                                            </div>
                                            <div className="text-right">
                                                <span className={`text-[10px] uppercase tracking-wider block mb-1 ${isLight ? "text-neutral-400" : "text-neutral-500"}`}>Check-out</span>
                                                <span className={`text-sm font-semibold ${isLight ? "text-neutral-900" : "text-white"}`}>{formatDate(selectedGuest.checkOut)}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <DetailField label="Guests" value={selectedGuest.guests} isLight={isLight} />
                                        <DetailField label="Channel" value={selectedGuest.channel} isLight={isLight} />
                                    </div>
                                    <div className="flex flex-col gap-1.5 w-full">
                                        <label className={`text-[10px] uppercase tracking-wider font-semibold ${isLight ? "text-neutral-400" : "text-neutral-500"}`}>Status</label>
                                        <div><StatusBadge status={selectedGuest.status} isLight={isLight} /></div>
                                    </div>
                                    <DetailField label="Notes" value={selectedGuest.note} isLight={isLight} multiline />

                                    <button
                                        onClick={startEditing}
                                        className={`w-full py-3 rounded-xl text-xs font-semibold mt-2 transition-transform active:scale-95 ${isLight ? "bg-neutral-900 text-white hover:bg-neutral-800" : "bg-white text-neutral-900 hover:bg-neutral-200"
                                            }`}>
                                        Edit Booking
                                    </button>
                                </>
                            ) : !isEditing && activeTab === 'messages' ? (
                                <div className="space-y-4">
                                    {loadingModalMessages ? (
                                        <div className="flex flex-col items-center justify-center py-20 opacity-50">
                                            <Loader2 size={24} className="animate-spin mb-2" />
                                            <p className="text-[10px] uppercase tracking-wider font-semibold">Loading history...</p>
                                        </div>
                                    ) : modalMessages.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-20 opacity-30">
                                            <MessageSquare size={32} className="mb-2" />
                                            <p className="text-[10px] uppercase tracking-wider font-semibold">No messages found</p>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col space-y-4">
                                            {modalMessages.map((msg, idx) => {
                                                const currentDateKey = getChatDateKey(msg.created_on);
                                                const prevDateKey = idx > 0 ? getChatDateKey(modalMessages[idx - 1].created_on) : null;
                                                const showDateSeparator = idx === 0 || currentDateKey !== prevDateKey;

                                                let userInput = msg.user_input_english || msg.user_input_original;
                                                let assistantOutput = msg.output_english || msg.output_original || msg.message;
                                                
                                                if (!userInput && !assistantOutput && msg.message) {
                                                    if (msg.role === 'user') userInput = msg.message;
                                                    else assistantOutput = msg.message;
                                                }

                                                return (
                                                    <React.Fragment key={msg.id || idx}>
                                                        {showDateSeparator && (
                                                            <div className="flex items-center justify-center my-4">
                                                                <div className={`px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider ${isLight ? "bg-neutral-100 text-neutral-500 border border-neutral-200" : "bg-white/5 text-neutral-500 border border-white/5"}`}>
                                                                    {formatFullChatDate(msg.created_on)}
                                                                </div>
                                                            </div>
                                                        )}
                                                        {userInput && (
                                                            <div className="flex justify-start">
                                                                <div className="max-w-[90%] flex flex-col gap-1">
                                                                    <div className={`px-3 py-2 rounded-2xl text-xs leading-relaxed ${isLight ? "bg-white text-neutral-900 border border-neutral-200" : "bg-white/5 text-neutral-100 border border-white/10"}`}>
                                                                        {linkify(userInput, false, isLight)}
                                                                    </div>
                                                                    <span className="text-[9px] font-medium opacity-40 ml-1">{formatChatTime(msg.created_on)}</span>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {assistantOutput && (
                                                            <div className="flex justify-end">
                                                                <div className="max-w-[90%] flex flex-col gap-1 text-right">
                                                                    <div className={`px-3 py-2 rounded-2xl text-xs leading-relaxed text-left ${isLight ? "bg-neutral-900 text-white" : "bg-white text-neutral-900"}`}>
                                                                        {linkify(assistantOutput, true, isLight)}
                                                                    </div>
                                                                    <span className="text-[9px] font-medium opacity-40 mr-1">{formatChatTime(msg.created_on)}</span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                /* Edit Mode */
                                <>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <FormField label="First Name" value={editedGuestData.name} onChange={(e) => handleEditChange('name', e.target.value)} isLight={isLight} />
                                        <FormField label="Last Name" value={editedGuestData.surname} onChange={(e) => handleEditChange('surname', e.target.value)} isLight={isLight} />
                                    </div>
                                    <FormField label="Reservation Code *" value={editedGuestData.reservationCode} onChange={(e) => handleEditChange('reservationCode', e.target.value)} isLight={isLight} />
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <FormField label="Email" value={editedGuestData.email} onChange={(e) => handleEditChange('email', e.target.value)} isLight={isLight} />
                                        <FormField label="Phone" value={editedGuestData.phone} onChange={(e) => handleEditChange('phone', e.target.value)} isLight={isLight} />
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <DateField label="Check-in" value={formatDateForInput(editedGuestData.checkIn)} onChange={(e) => handleEditChange('checkIn', e.target.value)} isLight={isLight} />
                                        <DateField label="Check-out" value={formatDateForInput(editedGuestData.checkOut)} onChange={(e) => handleEditChange('checkOut', e.target.value)} isLight={isLight} />
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <FormField label="Guests" type="number" value={editedGuestData.guests} onChange={(e) => handleEditChange('guests', parseInt(e.target.value) || 0)} isLight={isLight} />
                                        <FormField label="Channel" value={editedGuestData.channel} onChange={(e) => handleEditChange('channel', e.target.value)} isLight={isLight} />
                                    </div>
                                    <SelectField
                                        label="Status"
                                        isLight={isLight}
                                        value={editedGuestData.status}
                                        onChange={(e) => handleEditChange('status', e.target.value)}
                                        options={STATUS_OPTIONS}
                                    />
                                    <FormField label="Notes" value={editedGuestData.note} onChange={(e) => handleEditChange('note', e.target.value)} isLight={isLight} multiline />

                                    <div className="flex gap-3 mt-4">
                                        <button
                                            onClick={cancelEditing}
                                            className={`flex-1 py-3 rounded-xl text-xs font-semibold border ${isLight ? "border-neutral-200 text-neutral-600 hover:bg-neutral-50" : "border-white/10 text-neutral-400 hover:bg-white/5"
                                                }`}>
                                            Cancel
                                        </button>
                                        <button
                                            onClick={() => saveEditing(tabActiveHotelId, api)}
                                            className={`flex-[2] py-3 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 ${isLight ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-emerald-500 text-neutral-900 hover:bg-emerald-400"
                                                }`}>
                                            <Save size={14} />
                                            Save Changes
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

